import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, fmtDateLong, fmtTime, payLabel } from "@/lib/api";
import { MapPin } from "lucide-react";

export default function WorkerRequests() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/worker/requests");
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const act = async (requestId, action) => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/requests/${requestId}/${action}`);
      await load();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl" data-testid="worker-requests">
      <p className="overline">Requests</p>
      <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Coverage requests</h1>
      <p className="mt-3 text-sm text-slate-600">Pharmacies that asked you directly to cover a shift.</p>

      {error && <p className="mt-6 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      {items.length === 0 ? (
        <p className="card-surface mt-8 p-8 text-sm text-slate-600" data-testid="requests-empty">
          No pending requests right now.
        </p>
      ) : (
        <div className="mt-8 space-y-5">
          {items.map((s) => (
            <div key={s.request_id} className="card-surface p-6" data-testid={`request-card-${s.request_id}`}>
              <p className="overline">Coverage request</p>
              <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight">{s.pharmacy?.name}</h2>
              <p className="mt-1 flex items-center gap-2 text-sm text-slate-500">
                <MapPin className="h-4 w-4" /> {s.location_label}
                {s.distance != null && ` · ${s.distance} mi away`}
              </p>              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div>
                  <p className="overline">Date</p>
                  <p className="mt-1 font-medium">{fmtDateLong(s.date)}</p>
                </div>
                <div>
                  <p className="overline">Hours</p>
                  <p className="mt-1 font-medium">
                    {fmtTime(s.start_time)} – {fmtTime(s.end_time)}
                  </p>
                </div>
                <div>
                  <p className="overline">Pay</p>
                  <p className="mt-1 font-display text-xl font-semibold text-brand">{payLabel(s)}</p>
                </div>
              </div>
              {s.software_required?.length > 0 && (
                <p className="mt-3 text-sm text-slate-600">{s.software_required.join(", ")}</p>
              )}
              <p className="mt-4 text-sm text-slate-600">
                {s.pharmacy?.name} would like you to cover this {s.worker_role.toLowerCase()} shift.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <button className="btn-primary" disabled={busy} data-testid={`accept-${s.request_id}`} onClick={() => act(s.request_id, "accept")}>
                  Accept
                </button>
                <button className="btn-ghost" disabled={busy} data-testid={`decline-${s.request_id}`} onClick={() => act(s.request_id, "decline")}>
                  Decline
                </button>
                <Link to={`/worker/shifts/${s.shift_id}`} className="btn-ghost" data-testid={`view-${s.request_id}`}>
                  View shift
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
